import { type FC } from "react";
import logoSvg from "@/assets/img/FacilityAI_Logo_Mini.svg";
import "./sprite-sheet-loader.scss";

interface SpriteSheetLoaderProps {
	/**
	 * Size variant for the loader
	 * - small: 50px (for inline/compact spaces)
	 * - medium: 100px (default)
	 * - large: 150px (for full-page loaders)
	 */
	size?: "small" | "medium" | "large";
	
	/**
	 * Optional message to display below the loader
	 */
	message?: string;
	
	/**
	 * Animation speed in seconds (default: 1.5s)
	 * Lower values = faster animation
	 */
	speed?: number;
	
	/**
	 * Custom CSS class name
	 */
	className?: string;
	
	/**
	 * Center the loader in its container
	 */
	centered?: boolean;
}

/**
 * FacilityAI Custom Logo Loader
 * 
 * Displays the FacilityAI logo with a smooth pulsing animation.
 * 
 * @example
 * ```tsx
 * // Basic usage
 * <SpriteSheetLoader />
 * 
 * // With message and custom size
 * <SpriteSheetLoader size="small" message="Loading data..." />
 * 
 * // Faster animation
 * <SpriteSheetLoader speed={1} />
 * ```
 */
export const SpriteSheetLoader: FC<SpriteSheetLoaderProps> = ({
	size = "medium",
	message,
	speed = 1.5,
	className = "",
	centered = true,
}) => {
	const containerClass = centered 
		? "sprite-loader-container sprite-loader-centered" 
		: "sprite-loader-container";
	
	// Size configurations - maintaining 100x80 aspect ratio from SVG
	const sizeConfigs = {
		small: { width: 50, height: 40 },
		medium: { width: 100, height: 80 },
		large: { width: 150, height: 120 },
	};
	
	const config = sizeConfigs[size];
	
	return (
		<div className={`${containerClass} ${className}`}>
			<div className="sprite-loader-content">
				<div 
					className={`facility-loader facility-loader-${size}`}
					style={{
						animationDuration: `${speed}s`,
					}}
					role="status"
					aria-label="Loading"
				>
					<img 
						src={logoSvg} 
						alt="Loading" 
						width={config.width}
						height={config.height}
						style={{
							display: 'block',
						}}
					/>
				</div>
				{message && (
					<span className="sprite-loader-message">
						{message}
					</span>
				)}
			</div>
		</div>
	);
};

